import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CAPTCHA_FAILED, completeAuthSession, registerUser } from '../../store/slices/authSlice';
import { RecaptchaError, RecaptchaNotice, RecaptchaProvider, useRecaptchaState, useRecaptchaToken } from '../../hooks/useRecaptcha';
import Input from '../../components/ui/Input';
import Checkbox from '../../components/ui/Checkbox';
import robotIllustration from '../../assets/img/Illustration-Registration.png';
import { Header } from '../../components/layout/Header';
import LoginLayout from '../../components/layout/LoginLayout';
import { Footer } from '../../components/layout/Footer';
import type { AppDispatch, RootState } from '../../store/store';
import { helper } from '../../utils/helper';
import { addNotification } from '../../store/slices/notificationSlice';
import { useTranslation } from 'react-i18next';
import { getStoredReferralCode, clearStoredReferralCode } from '../../utils/referralCookies';
import { trackCompleteRegistration } from '../../utils/metaPixel';

const RegisterPageInner: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const getRecaptchaToken = useRecaptchaToken();
  const recaptchaState = useRecaptchaState();
  // Inline captcha_failed_help notice: set on 422 or a failed token fetch.
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const { isAuthenticated, loading } = useSelector((state: RootState) => state.auth);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  // Referral code from the `s2g_ref` cookie (read once on mount). When
  // present, it is sent silently and the manual partner-code field is hidden.
  const [cookieReferralCode] = useState<string | null>(() => getStoredReferralCode());
  const [showPartnerCodeField, setShowPartnerCodeField] = useState(false);
  const [partnerCode, setPartnerCode] = useState('');
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    repeatPassword?: string;
    terms?: string;
  }>({});

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/profile?firstTime=true');
    }
  }, [isAuthenticated, navigate]);

  const validatePassword = useCallback((value: string) => {
    if (!value) {
      return t('validation_password_required');
    }
    if (value.length < 6) {
      return t('validation_password_short');
    }
    return '';
  }, [t]);

  const validateRepeatPassword = useCallback((value: string) => {
    if (!value) {
      return t('validation_password_required');
    }
    if (value !== password) {
      return t('validation_password_mismatch');
    }
    return '';
  }, [password, t]);

  const handleEmailBlur = () => {
    setErrors(prev => ({
      ...prev,
      email: helper.validateEmail(email) ? t('validation_email_invalid') : ''
    }));
  };

  const handlePasswordBlur = () => {
    setErrors(prev => ({
      ...prev,
      password: validatePassword(password)
    }));
  };

  const handleRepeatPasswordBlur = () => {
    setErrors(prev => ({
      ...prev,
      repeatPassword: validateRepeatPassword(repeatPassword)
    }));
  };

  const handleAgreeTermsChange = (checked: boolean) => {
    setAgreeTerms(checked);
    if (checked) {
      setErrors(prev => ({
        ...prev,
        terms: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      email: helper.validateEmail(email) ? t('validation_email_invalid') : '',
      password: validatePassword(password),
      repeatPassword: validateRepeatPassword(repeatPassword),
      terms: agreeTerms ? '' : t('validation_terms_required')
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setCaptchaFailed(false);

    // A fresh token per submit: v3 tokens are single-use.
    let captchaToken: string | null = null;
    try {
      captchaToken = await getRecaptchaToken('register');
    } catch (err) {
      if (err instanceof RecaptchaError) {
        // Do not send the request without a token - it is guaranteed to 422.
        setCaptchaFailed(true);
        return;
      }
      throw err;
    }

    try {
      const referralCode = cookieReferralCode || partnerCode.trim() || null;
      const result = await dispatch(registerUser({ email, password, referralCode, captchaToken }));
      
      if (registerUser.fulfilled.match(result)) {
        trackCompleteRegistration();
        // The referral is consumed by this registration - drop the cookie so
        // it no longer affects later visits (login-vs-register redirect).
        if (cookieReferralCode) {
          clearStoredReferralCode();
        }
        // Persist token, prime OpenAPI and run the bootstrap (profile, ds,
        // subscriptions, workspace-config) before routing - same pattern as
        // LoginPage. Without this the new user's dashboard would render
        // before any of those requests finish.
        await dispatch(completeAuthSession(result.payload));
        dispatch(addNotification({
          type: 'success',
          title: t('notification_success'),
          message: t('registration_success'),
        }));
        navigate('/profile?firstTime=true');
      } else if (registerUser.rejected.match(result)) {
        if (result.payload === CAPTCHA_FAILED) {
          // Inline notice is the requirement; the toast is kept on top of it.
          setCaptchaFailed(true);
        }
        dispatch(addNotification({
          type: 'error',
          title: t('notification_error'),
          message: result.payload === CAPTCHA_FAILED
            ? t('captcha_failed')
            : (result.payload || t('registration_failed')),
        }));
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: t('registration_failed'),
      }));
    }
  };

  return (
    <LoginLayout>
      <div className="login-illustration">
        <img src={robotIllustration} alt={t('registerAccount')} />
      </div>

      <Header isLoginHeader={true} showLoginHeader={true} />
      
      <div className="login-content">
        <div className="login-form-container">
          <div className="login-titles">
            <h1>{t('registerAccount')}</h1>
            <p>{t('iHaventAccount')}</p>
          </div>
          
          <form onSubmit={handleRegister} className="login-form">
            <Input
              id="email"
              label={t('emailAddress')}
              type="email"
              value={email}
              error={errors.email}
              onChange={setEmail}
              onBlur={handleEmailBlur}
              required
            />
            
            <Input
              id="password"
              label={t('password')}
              type="password"
              value={password}
              error={errors.password}
              onChange={setPassword}
              onBlur={handlePasswordBlur}
              required
            />

            <Input
              id="repeatPassword"
              label={t('repeatPasswordLabel')}
              type="password"
              value={repeatPassword}
              error={errors.repeatPassword}
              onChange={setRepeatPassword}
              onBlur={handleRepeatPasswordBlur}
              required
            />

            {/* Optional partner code - only when no referral cookie is set.
                With a cookie the code is sent silently. */}
            {!cookieReferralCode && (
              showPartnerCodeField ? (
                <Input
                  id="partnerCode"
                  label={t('affiliate.register_partner_code_label')}
                  value={partnerCode}
                  onChange={setPartnerCode}
                />
              ) : (
                <button
                  type="button"
                  className="affiliate-link-button"
                  onClick={() => setShowPartnerCodeField(true)}
                >
                  {t('affiliate.register_partner_code_toggle')}
                </button>
              )
            )}

            <Checkbox
              checked={agreeTerms}
              onChange={handleAgreeTermsChange}
              error={errors.terms}
            >
              {t('iAgreeWithThe')} <a target="_blank" rel="noopener noreferrer" href="https://seller2go.com/agb/" className="terms-link">{t('footerTerms')}</a> {t('de_zu')} 
            </Checkbox>
            
            {recaptchaState === 'blocked' && <RecaptchaNotice variant="blocked" />}
            {recaptchaState !== 'blocked' && captchaFailed && <RecaptchaNotice variant="failed" />}

            <button 
              type="submit" 
              className="login-button"
              disabled={loading || recaptchaState !== 'ready'}
            >
              {loading || recaptchaState === 'loading' ? t('notification_loading') : t('registerAccount')}
            </button>
            
            <Link to="/login" className="registration-link">
              {t('alreadyHaveAnAccountText')} {t('login')}
            </Link>
          </form>
        </div>
      </div>
      <Footer />
    </LoginLayout>
  );
};

const RegisterPage: React.FC = () => (
  <RecaptchaProvider>
    <RegisterPageInner />
  </RecaptchaProvider>
);

export default RegisterPage;
